import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Download } from 'lucide-react';
import { employeesAPI } from '../../services/api';
import { useCompany } from '../common/useCompany';
import PrintButton from '../../utils/print';
import PrintLetterhead from '../common/PrintLetterhead';
import EmailModal from '../common/EmailModal';
import { generatePaySlipPDF } from '../../utils/generateDocumentPDF';
import toast from 'react-hot-toast';
import { fmtMoney } from '../../utils/currency';

const fmt = fmtMoney;
const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const FREQUENCY_LABELS = {
  Weekly: 'Weekly', BiWeekly: 'Bi-Weekly', SemiMonthly: 'Semi-Monthly', Monthly: 'Monthly',
};

const PERIODS_PER_YEAR = {
  Weekly: 52, BiWeekly: 26, SemiMonthly: 24, Monthly: 12,
};

const TAX_RATE = 0.22; // Estimated federal tax rate for payslip display
const FICA_RATE = 0.0765; // Social Security + Medicare

export default function PaySlip() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const company = useCompany();
  const payslipRef = useRef(null);
  const [showEmail, setShowEmail] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedId, setSelectedId] = useState(searchParams.get('employeeId') || '');
  const [loading, setLoading] = useState(true);
  const [payPeriodStart, setPayPeriodStart] = useState(() => {
    const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0];
  });
  const [payPeriodEnd, setPayPeriodEnd] = useState(() => {
    const d = new Date(); return d.toISOString().split('T')[0];
  });

  useEffect(() => {
    employeesAPI.list()
      .then(list => { setEmployees(list); if (!selectedId && list.length > 0) setSelectedId(String(list[0].id)); })
      .catch(e => toast.error(e.message))
      .finally(() => setLoading(false));
  }, []);

  const emp = employees.find(e => String(e.id) === String(selectedId));

  // Calculate payslip amounts
  const grossPay = emp ? (parseFloat(emp.salary) || 0) / (PERIODS_PER_YEAR[emp.pay_frequency] || 12) : 0;
  const federalTax = grossPay * TAX_RATE;
  const fica = grossPay * FICA_RATE;
  const totalDeductions = federalTax + fica;
  const netPay = grossPay - totalDeductions;

  return (
    <div>
      <div className="toolbar">
        <h2>💰 Pay Slip</h2>
        <div className="toolbar-right">
          <PrintButton targetRef={payslipRef} title={`Pay Slip - ${emp ? emp.first_name + ' ' + emp.last_name : ''}`} />
          {emp && (
            <>
              <button className="btn btn-outline" onClick={async () => { try { await generatePaySlipPDF(emp, { grossPay, federalTax, fica, netPay, taxRate: TAX_RATE * 100, payPeriod: `${payPeriodStart} — ${payPeriodEnd}` }, company); } catch(e) { toast.error('PDF failed: ' + e.message); } }} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <Download size={14} /> PDF
              </button>
              <button className="btn btn-outline" onClick={() => setShowEmail(true)} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <Mail size={14} /> Email
              </button>
            </>
          )}
          <button className="btn btn-outline" onClick={() => navigate('/employees')}>Back to Employees</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 20 }}>
        {/* Controls */}
        <div className="df-editor" style={{ flex: '0 0 320px' }}>
          <div className="df-section-header"><span>Pay Slip Settings</span></div>
          <div className="df-body">
            {loading ? <div className="spinner" /> : (
              <div className="df-grid" style={{ gridTemplateColumns: '1fr' }}>
                <div className="df-field">
                  <label>Employee <span className="req">*</span></label>
                  <select required value={selectedId} onChange={e => setSelectedId(e.target.value)}>
                    <option value="">Select employee...</option>
                    {employees.filter(e => e.is_active).map(e => (
                      <option key={e.id} value={e.id}>{e.first_name} {e.last_name} ({e.employee_number})</option>
                    ))}
                  </select>
                </div>
                <div className="df-field">
                  <label>Pay Period Start</label>
                  <input type="date" value={payPeriodStart} onChange={e => setPayPeriodStart(e.target.value)} />
                </div>
                <div className="df-field">
                  <label>Pay Period End</label>
                  <input type="date" value={payPeriodEnd} onChange={e => setPayPeriodEnd(e.target.value)} />
                </div>
              </div>
            )}

            {emp && (
              <>
                <div style={{ marginTop: 16, padding: 12, background: '#f0f9ff', borderRadius: 4, border: '1px solid #bae6fd' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', marginBottom: 8 }}>Employee Summary</div>
                  <div style={{ fontSize: 12, lineHeight: 1.8 }}>
                    <div><strong>Name:</strong> {emp.first_name} {emp.last_name}</div>
                    <div><strong>Title:</strong> {emp.job_title || '—'}</div>
                    <div><strong>Department:</strong> {emp.department || '—'}</div>
                    <div><strong>Annual Salary:</strong> {fmt(emp.salary)}</div>
                    <div><strong>Pay Frequency:</strong> {FREQUENCY_LABELS[emp.pay_frequency] || emp.pay_frequency}</div>
                    <div><strong>Periods/Year:</strong> {PERIODS_PER_YEAR[emp.pay_frequency] || 12}</div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Payslip Preview */}
        <div style={{ flex: 1 }} ref={payslipRef}>
          {emp ? (
            <div className="card print-preview" style={{ padding: 0, overflow: 'hidden' }}>
              <PrintLetterhead />
              <div style={{ padding: '20px 24px' }}>
                {/* Title */}
                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                  <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1e293b' }}>EMPLOYEE PAY SLIP</h2>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                    Pay Period: {fmtDate(payPeriodStart)} — {fmtDate(payPeriodEnd)}
                  </div>
                </div>

                {/* Employee Info */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20, padding: '12px 16px', background: '#f8fafc', borderRadius: 4, border: '1px solid #e2e8f0' }}>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>Employee</div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{emp.first_name} {emp.last_name}</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>Employee No.</div>
                    <div style={{ fontSize: 13, fontFamily: 'monospace' }}>{emp.employee_number}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>Department</div>
                    <div style={{ fontSize: 13 }}>{emp.department || '—'}</div>
                  </div>
                </div>

                {/* Earnings & Deductions */}
                <div style={{ display: 'flex', gap: 20, marginBottom: 20 }}>
                  {/* Earnings */}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#16a34a', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8, paddingBottom: 4, borderBottom: '2px solid #16a34a' }}>Earnings</div>
                    <table style={{ width: '100%', fontSize: 12 }}>
                      <tbody>
                        <tr><td style={{ padding: '6px 0' }}>Base Salary</td><td style={{ textAlign: 'right', padding: '6px 0', fontWeight: 600 }}>{fmt(grossPay)}</td></tr>
                        <tr style={{ borderTop: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '8px 0', fontWeight: 700 }}>Gross Pay</td>
                          <td style={{ textAlign: 'right', padding: '8px 0', fontWeight: 700, fontSize: 14 }}>{fmt(grossPay)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Deductions */}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#dc2626', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8, paddingBottom: 4, borderBottom: '2px solid #dc2626' }}>Deductions</div>
                    <table style={{ width: '100%', fontSize: 12 }}>
                      <tbody>
                        <tr><td style={{ padding: '6px 0' }}>Federal Tax (est. {(TAX_RATE * 100).toFixed(0)}%)</td><td style={{ textAlign: 'right', padding: '6px 0' }}>{fmt(federalTax)}</td></tr>
                        <tr><td style={{ padding: '6px 0' }}>FICA — SS & Medicare ({(FICA_RATE * 100).toFixed(1)}%)</td><td style={{ textAlign: 'right', padding: '6px 0' }}>{fmt(fica)}</td></tr>
                        <tr style={{ borderTop: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '8px 0', fontWeight: 700 }}>Total Deductions</td>
                          <td style={{ textAlign: 'right', padding: '8px 0', fontWeight: 700, fontSize: 14 }}>{fmt(totalDeductions)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Net Pay */}
                <div style={{ background: '#0f2747', color: '#fff', borderRadius: 4, padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.5 }}>NET PAY</div>
                  <div style={{ fontSize: 24, fontWeight: 700, fontFamily: 'monospace' }}>{fmt(netPay)}</div>
                </div>

                {/* Footer note */}
                <div style={{ marginTop: 16, fontSize: 10, color: '#94a3b8', textAlign: 'center' }}>
                  This is a computer-generated pay slip. Taxes shown are estimated. Consult your tax advisor for actual withholding amounts.
                </div>
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-body" style={{ padding: 40, textAlign: 'center' }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>💰</div>
                <h3 style={{ color: '#64748b' }}>Select an employee to generate a pay slip</h3>
                <p style={{ color: '#94a3b8', fontSize: 12 }}>Choose an employee from the dropdown on the left to preview their pay slip.</p>
              </div>
            </div>
          )}
        </div>
      </div>
      <EmailModal
        show={showEmail}
        onClose={() => setShowEmail(false)}
        type="payslip"
        refId={emp?.id}
        number={emp?.employee_number}
        recipientEmail={emp?.email || ''}
        recipientName={emp ? emp.first_name + ' ' + emp.last_name : ''}
      />
    </div>
  );
}
